/** ``source_kind`` of rows the editor contributed: clips cut from a document,
 *  and whole files (a recording with its corrected transcript). Mirrors
 *  ``CONTRIBUTION_KINDS`` in the API. */
export const CONTRIBUTION_KINDS = new Set(['editor_clip', 'editor_file']);

export const isContribution = (sourceKind?: string | null): boolean =>
  !!sourceKind && CONTRIBUTION_KINDS.has(sourceKind);

import type { ContributeReadiness } from '@utils/api/custom-models/use-contribute-segment.tsx';
import { formatClipDuration } from './training-clip-windows';

/** A translation key and its values, for the component to pass to ``t``. */
export type Message = { key: string; values?: Record<string, string | number> };

/**
 * What a contribution did, in one line. A contribution can add clips, rewrite
 * the text of clips already there, or add or refresh a whole file, and the
 * customer needs to know which, and where it went.
 */
export const contributionResultMessage = (
  result: ContributeReadiness,
  model?: string
): Message => {
  const where = model ? { model } : undefined;
  if (!where) return { key: 'contribution-received' };
  if (result.file_added) {
    return {
      key: 'contribution-whole-added',
      values: {
        ...where,
        duration: formatClipDuration((result.seconds_transcribed ?? 0) * 1000)
      }
    };
  }
  if (result.file_updated)
    return { key: 'contribution-whole-updated', values: where };
  const added = result.clips_added ?? 0;
  const updated = result.clips_updated ?? 0;
  const duration = formatClipDuration((result.seconds_added ?? 0) * 1000);
  if (added && updated) {
    return {
      key: 'contribution-added-and-updated',
      values: { ...where, duration, count: updated }
    };
  }
  if (updated) {
    return {
      key: 'contribution-updated-only',
      values: { ...where, count: updated }
    };
  }
  if (added)
    return {
      key: 'contribution-added-duration',
      values: { ...where, duration }
    };
  return { key: 'contribution-received-model', values: where };
};

/**
 * Progress towards the next round in minutes of audio, the rule training
 * checks. Counting clips misled once one contribution could add many clips of
 * different lengths, and a whole file counts as a single clip.
 */
export const contributionProgress = (result: ContributeReadiness) => {
  const total = result.total_duration_seconds ?? 0;
  const required = result.required_duration_seconds ?? 0;
  return {
    pct:
      required > 0 ? Math.min(100, Math.round((total / required) * 100)) : 100,
    hasEnough: total >= required,
    current: (total / 60).toFixed(1),
    required: (required / 60).toFixed(0)
  };
};
